import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Info } from 'styled-icons/octicons/Info';

import { Text, Row, Col, FlexView, Button, Separator } from '@cuemath/comps';
import COLORS from 'constants/colors';
import {
  COLUMN_NAMES,
  buttonActions,
  BUTTON_NAME,
  averageData,
  FORM_FIELDS_COUNT,
} from 'constants/reviews';
import { Asterisk } from 'modules/form';
import Tooltip from 'comps/tooltip';
import FeedbackSection from './feedback';
import LogDetails from './log-details';
import DemoActionPoint from 'img/demo-action-points.png';

const FlexWrapper = styled(FlexView)`
  box-sizing: border-box;
  flex: 0 0 350px;
  padding-right: 0.5rem;
  padding-left: 0.5rem;
  padding: 0px 0px;
  box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.1);
  width: 295px;
  max-height: inherit;
  min-height: calc(100vh - 50px);
`;

const ButtonWrap = styled(Col)`
  position: relative;
  margin-left: 10px;
`;

const TextStyle = styled(Text)`
  padding-right: 8px;
  padding-left: 3px;
  margin-top: 2px;
  margin-left: 10px;
  opacity: 0.5;
  font-family: 'Open Sans';
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  line-height: 19px;
`;

const ColMargin = styled(Col)`
  position: relative;
  left: 6px;
`;

export const Message = styled.div`
  width: 100%;
  margin-top: 30px;
  border: 1px solid;
  border-radius: 5px;
  padding: 5px;
  text-align: center;
  background: ${({ error }) => (error ? COLORS.ERROR : COLORS.SUCCESS)};
`;

const TextArea = styled.textarea`
  height: 55px;
  width: 400px;
  margin-bottom: 8px;
  background-color: ${COLORS.WHITE};
  font-size: 14px;
  border: 1.4px solid ${COLORS.PRIMARY};
  color: ${COLORS.BLACK};
  margin-left: 10px;
  overflow-y: scroll;
  resize: none;
  cursor: auto;
`;

const CommentStyle = styled(Text)`
  margin-left: 10px;
`;

const AVLogTitle = styled(CommentStyle)`
  margin-left: 10px;
  font-weight: bold;
  font-size: 16px;
`;

const ButtonRow = styled(Row)`
  margin: 0 auto;
  padding-top: 15%;
`;

const RowWrap = styled(Row)`
  flex-wrap: nowrap;
`;

const isNull = arr => {
  return arr.join().replace(/,/g, '').length === 0;
};

const canPublish = (
  isFormDisabled,
  result,
  questionRes,
  remarks,
  actionPoints,
  isDemoReview,
) => {
  if (isFormDisabled) {
    // allow unpublish when for is already published
    return true;
  }

  let ratingsCount = Object.keys(questionRes).length;

  let commentsCount = Object.keys(remarks).reduce((commentsCount, key) => {
    if (remarks[key] && remarks[key].trim() !== '') commentsCount += 1;
    return commentsCount;
  }, 0);

  if (result) {
    ratingsCount += result.filter(({ id }) => !questionRes[id]).length;
    commentsCount += result.filter(
      ({ id, comments }) => !remarks[id] && comments && comments.trim() !== '',
    ).length;
  }
  return (
    ratingsCount === FORM_FIELDS_COUNT &&
    commentsCount === FORM_FIELDS_COUNT &&
    (isDemoReview || (actionPoints && actionPoints.trim() !== ''))
  );
};

const DemoActionPointImg = styled.img`
  border-radius: 15px;
`;

const ActionPointsInfo = () => (
  <DemoActionPointImg src={DemoActionPoint} width="300" alt="Demo Action Points" />
);

const ClasroomForm = props => {
  let {
    broadcastCallLogData,
    classLogs,
    demoActions,
    demoActionsError,
    errorMsg,
    getLogValues,
    getRadioValues,
    groupCallLogData,
    handleAverageChange,
    handleOptionChange,
    id,
    isDemoReview,
    isFormDisabled,
    isQuestionRes,
    isSales,
    logsReviewed,
    onActionChange,
    onCommentChange,
    onDemoActionChange,
    onMessageChange,
    onLogClicked,
    onPlusClicked,
    onReviewThisSelected,
    onSubmitAction,
    plusValues,
    questionRes,
    questionsData,
    remark,
    result,
    reviewClass,
    reviewStatus,
    reviewSubmitted,
    savedComments,
    selectedAvg,
    selectedTeacherActions,
  } = props;

  const {
    broadcast_logs_count: broadcastLogsCount = 0,
    broadcast_logs: broadcastLogs,
    broadcast_logs_duration: broadcastLogsDuration,
  } = broadcastCallLogData || {};

  const {
    group_logs_count: groupLogsCount = 0,
    group_logs: groupLogs,
    group_logs_duration: groupLogsDuration,
  } = groupCallLogData || {};

  let studentCallCount;
  let screenLogsCount;
  if (classLogs && classLogs.length > 0) {
    let {
      student_call_count: studentCallCountTemp,
      screen_logs_count: screenLogsCountTemp,
    } = classLogs[0];
    studentCallCount = studentCallCountTemp;
    screenLogsCount = screenLogsCountTemp;
  }
  let totalLogsCount =
    screenLogsCount + studentCallCount + groupLogsCount + broadcastLogsCount;

  const plusExists =
    plusValues &&
    Object.keys(plusValues).length > 0 &&
    !isNull(Object.values(plusValues)) &&
    plusValues.constructor === Object;

  const isPlusValue =
    plusExists || (logsReviewed && Array.isArray(logsReviewed) && logsReviewed.length);

  const disablePublish = !canPublish(
    isFormDisabled,
    result,
    questionRes,
    remark,
    selectedTeacherActions,
    isDemoReview,
  );

  let plusObj = {};

  if (plusValues && logsReviewed && Array.isArray(logsReviewed) && logsReviewed.length) {
    plusObj = getLogValues(plusValues, logsReviewed);
  } else {
    plusObj = plusValues;
  }

  const reviewClassWrapper = () => {
    if (!isSales)
      return (
        <ButtonRow>
          <Button label="REVIEW THIS CLASS" onClick={onReviewThisSelected} />
        </ButtonRow>
      );
    return null;
  };

  return (
    <div>
      <RowWrap>
        <FlexWrapper>
          {classLogs && Array.isArray(classLogs) && classLogs.length > 0 && (
            <>
              <AVLogTitle>Student Call</AVLogTitle>
              <Separator height={1} transparent />
              <LogDetails
                isFormDisabled={isFormDisabled}
                isGroupLog={false}
                logsReviewed={logsReviewed}
                onLogClicked={onLogClicked}
                onPlusClicked={onPlusClicked}
                plusValues={plusObj}
                studentCallLogs={classLogs}
                totalLogsCount={totalLogsCount}
              />
            </>
          )}
          <Separator height={3} transparent />
          {broadcastLogsCount > 0 && (
            <>
              <AVLogTitle>Broadcast</AVLogTitle>
              <Separator height={1} transparent />
              <LogDetails
                classCallCount={broadcastLogsCount}
                classCallDuration={broadcastLogsDuration}
                classCallLogs={broadcastLogs}
                isFormDisabled={isFormDisabled}
                isGroupLog
                logsReviewed={logsReviewed}
                onLogClicked={onLogClicked}
                onPlusClicked={onPlusClicked}
                plusValues={plusObj}
                totalLogsCount={totalLogsCount}
              />
            </>
          )}
          <Separator height={3} transparent />
          {groupLogsCount > 0 && (
            <>
              <AVLogTitle>Group Call</AVLogTitle>
              <Separator height={1} transparent />
              <LogDetails
                classCallCount={groupLogsCount}
                classCallDuration={groupLogsDuration}
                classCallLogs={groupLogs}
                isFormDisabled={isFormDisabled}
                isGroupLog
                logsReviewed={logsReviewed}
                onLogClicked={onLogClicked}
                onPlusClicked={onPlusClicked}
                plusValues={plusObj}
                totalLogsCount={totalLogsCount}
              />
            </>
          )}
        </FlexWrapper>

        {!reviewClass ? (
          reviewClassWrapper()
        ) : (
          <Col>
            <div>
              <Row>
                <Col xs={4} sm={4} md={4} lg={4}>
                  <TextStyle>{COLUMN_NAMES.PARAMETERS}</TextStyle>
                </Col>
                <ColMargin xs={4} sm={4} md={4} lg={4}>
                  <TextStyle>
                    {COLUMN_NAMES.RATINGS}
                    <Asterisk>*</Asterisk>
                  </TextStyle>
                </ColMargin>
                <Col xs={4} sm={4} md={4} lg={4}>
                  <TextStyle>
                    {COLUMN_NAMES.COMMENTS}
                    <Asterisk>*</Asterisk>
                  </TextStyle>
                </Col>
              </Row>
              <FeedbackSection
                radioOptions={questionsData}
                handleOptionChange={handleOptionChange}
                onRemarkChange={onMessageChange}
                remark={remark}
                stateValue={
                  isQuestionRes && !reviewSubmitted ? questionRes : getRadioValues(result)
                }
                disabled={isFormDisabled}
              />
              <Separator height={2} />
              <Separator height={20} transparent />
              <Row>
                <Col>
                  <CommentStyle>
                    Comments on Flag or Celebrated Logs (optional)
                  </CommentStyle>
                  <TextArea
                    value={savedComments}
                    onChange={onCommentChange}
                    disabled={isFormDisabled}
                    key={`comment-${id}`}
                    id={`comment-${id}`}
                  />
                </Col>
                {!isDemoReview && (
                  <Col>
                    <CommentStyle>
                      Action Points (For Teacher)
                      <Asterisk>*</Asterisk>
                    </CommentStyle>
                    <TextArea
                      value={selectedTeacherActions}
                      onChange={onActionChange}
                      disabled={isFormDisabled}
                      key={`actions-${id}`}
                      id={`actions-${id}`}
                    />
                  </Col>
                )}
              </Row>
              {isDemoReview && (
                <>
                  <Separator height={2} />
                  <Separator height={20} transparent />
                  <Row>
                    <Col>
                      <CommentStyle>
                        Action points to show teacher on Pre-Demo screen&nbsp;
                        <Tooltip
                          title={<ActionPointsInfo />}
                          position="bottom"
                          width="300px"
                          bgColor={null}
                        >
                          <Info size={14} />
                        </Tooltip>
                      </CommentStyle>
                      {demoActions.map((demoAction, index) => (
                        <div key={`demo-action-${index + 1}`}>
                          <TextArea
                            value={demoAction}
                            onChange={e => {
                              onDemoActionChange(e.target.value, index);
                            }}
                            disabled={isFormDisabled}
                            id={`demo-action-${index + 1}`}
                            placeholder={`Action point${index + 1}`}
                            key={`demo-action-${index + 1}`}
                          />
                        </div>
                      ))}
                      {demoActionsError && (
                        <Row>
                          <Text color={COLORS.NEGATIVE}>{demoActionsError}</Text>
                        </Row>
                      )}
                    </Col>
                  </Row>
                </>
              )}
              <Separator height={2} />
              {errorMsg && <Message error>{errorMsg}</Message>}
              <Row justifyContent="space-between">
                <Col>
                  <FeedbackSection
                    radioOptions={averageData.questions}
                    handleOptionChange={handleAverageChange}
                    stateValue={selectedAvg}
                    disabled={isFormDisabled}
                  />
                </Col>

                <Col>
                  <Separator height={20} transparent />
                  <Row justifyContent="flex-end">
                    <ButtonWrap>
                      <Button
                        label="SAVE"
                        disabled={isFormDisabled}
                        onClick={() => onSubmitAction(buttonActions.SAVE)}
                      />
                    </ButtonWrap>
                    <ButtonWrap>
                      <Button
                        label={BUTTON_NAME[reviewStatus]}
                        disabled={disablePublish || !isPlusValue}
                        onClick={() => onSubmitAction(BUTTON_NAME[reviewStatus])}
                      />
                    </ButtonWrap>
                  </Row>
                  <Separator height={10} transparent />
                  {disablePublish && (
                    <Text>
                      Please fill in all the mandatory fields to Publish
                      <Asterisk>*</Asterisk>
                    </Text>
                  )}
                  <Separator height={50} transparent />
                </Col>
              </Row>
            </div>
          </Col>
        )}
      </RowWrap>
    </div>
  );
};

ClasroomForm.propTypes = {
  broadcastCallLogData: PropTypes.shape({}),
  classLogs: PropTypes.array,
  groupCallLogData: PropTypes.shape({}),
  id: PropTypes.string,
  isFormDisabled: PropTypes.bool.isRequired,
  isQuestionRes: PropTypes.bool.isRequired,
  logsReviewed: PropTypes.array,
  onLogClicked: PropTypes.func.isRequired,
  onPlusClicked: PropTypes.func.isRequired,
  plusValues: PropTypes.object.isRequired,
  questionRes: PropTypes.object.isRequired,
  questionsData: PropTypes.array,
  remark: PropTypes.object.isRequired,
  result: PropTypes.array,
  reviewClass: PropTypes.bool,
  reviewStatus: PropTypes.string,
  savedComments: PropTypes.array,
  selectedAvg: PropTypes.object.isRequired,
  selectedTeacherActions: PropTypes.string,
};

ClasroomForm.defaultProps = {
  broadcastCallLogData: null,
  classLogs: null,
  groupCallLogData: null,
  id: null,
  logsReviewed: null,
  questionsData: null,
  result: null,
  reviewClass: false,
  reviewStatus: null,
  savedComments: null,
  selectedTeacherActions: null,
};

export default ClasroomForm;
